import type { BatchLead, BatchNote } from './types';

/**
 * Pure helpers for the Batch grid's Notes column (port plan step 5): its
 * copy, the "+N earlier notes" count, what a failed save says, and how note
 * saves the server may not have seen yet are laid over a fresh list load.
 * No React, no I/O.
 */

/** The backend's RawnoteCreateSchema / RawnoteEditSchema cap on `text`, after trim. */
export const NOTE_TEXT_MAX = 20_000;

export const NOTES_COPY = {
  /** An editable cell with no note (the "+" is an icon). */
  addNote: 'Add a note',
  /** "+1 earlier note", "+3 earlier notes" — opens the lead. */
  earlier: (n: number) => `+${n} earlier note${n === 1 ? '' : 's'}`,
  placeholder: 'Type a note',
  hint: 'Click away or ⌘/Ctrl+Enter to save · Esc cancels',
  saving: 'Saving…',
  /** 409: the note is version-locked and someone else saved it first. */
  conflict: 'Someone else changed this note. Open the lead to see the latest.',
  /** 404 on an edit: the note (or its lead) is gone. */
  noteGone: 'This note was deleted. Open the lead to see the latest.',
  /** 404 on a create: the lead itself is gone. */
  leadGone: 'This lead was deleted',
  tooLong: `Use ${NOTE_TEXT_MAX.toLocaleString('en-US')} characters or fewer`,
  rateLimited: 'Too many saves at once. Wait a moment, then try again.',
  network: 'Couldn’t reach the server. Check your connection, then try again.',
  server: 'Couldn’t save: the server had a problem. Try again in a moment.',
  forbidden: 'You don’t have permission to edit this lead’s notes.',
  fallback: 'Couldn’t save the note.',
} as const;

/**
 * How many notes the "+N earlier notes" link counts: every live note but the
 * one the cell shows. Read from the existing `notesCount`, which also counts
 * lead-move notes (D11, accepted): a moved lead with one rep note shows
 * "+1 earlier note", and that one is the move record.
 */
export function earlierNotesCount(lead: Pick<BatchLead, 'latestNote' | 'notesCount'>): number {
  return Math.max(0, (lead.notesCount ?? 0) - (lead.latestNote ? 1 : 0));
}

function statusOf(err: unknown): number | undefined {
  const e = err as { statusCode?: unknown; status?: unknown } | null | undefined;
  const s = e?.statusCode ?? e?.status;
  return typeof s === 'number' && s > 0 ? s : undefined;
}

/**
 * What a failed note save says. Never raw status text; a 400 keeps the
 * server's own words (a validation message) when it sent any.
 */
export function noteSaveFailureMessage(err: unknown, kind: 'create' | 'edit'): string {
  const s = statusOf(err);
  if (s === 409) return NOTES_COPY.conflict;
  if (s === 404) return kind === 'edit' ? NOTES_COPY.noteGone : NOTES_COPY.leadGone;
  if (s === 429) return NOTES_COPY.rateLimited;
  if (s === 403) return NOTES_COPY.forbidden;
  if (s === undefined) return NOTES_COPY.network;
  if (s >= 500) return NOTES_COPY.server;
  const m = (err as { message?: unknown } | null | undefined)?.message;
  return s === 400 && typeof m === 'string' && m.trim() !== '' ? m : NOTES_COPY.fallback;
}

/** One grid note save, stamped with the order it was confirmed in. */
export interface NoteSave {
  /** Confirmation order, compared with the value a list load started at. */
  seq: number;
  note: BatchNote;
  /** The save created the note (the row's `notesCount` gains one). */
  created: boolean;
}

/** Whether a freshly loaded row predates `save`, so the save must be shown over it. */
function rowPredatesSave(row: BatchLead, save: NoteSave): boolean {
  const shown = row.latestNote;
  if (shown === undefined) return false; // a backend without the field: nothing to correct
  if (shown === null) return true;
  if (shown.id === save.note.id) return shown.serverVersion < save.note.serverVersion;
  // A different note on the row: an older one means the read missed our new
  // note; a newer one (another rep, the phone) wins.
  return save.created && shown.createdAt < save.note.createdAt;
}

/**
 * Forgets the saves a list load already carries: `since` is the save counter
 * when the load started, and a save confirmed before then is in what it
 * returned. Mutates `saves`.
 */
export function pruneNoteSaves(saves: Map<string, NoteSave>, since: number): void {
  for (const [id, save] of saves) if (save.seq <= since) saves.delete(id);
}

/**
 * Shows each save over a row that predates it (judged by the note's id,
 * version and age, so a row that already carries the save is left alone and
 * its count is never bumped twice). Only `latestNote` and `notesCount` are
 * touched; returns `rows` itself when nothing applies. Leaves `saves` alone.
 */
export function overlayNoteSaves(rows: BatchLead[], saves: ReadonlyMap<string, NoteSave>): BatchLead[] {
  if (saves.size === 0) return rows;
  let changed = false;
  const out = rows.map((row) => {
    const save = saves.get(row.id);
    if (!save || !rowPredatesSave(row, save)) return row;
    changed = true;
    const gainsOne = save.created && row.latestNote?.id !== save.note.id;
    return {
      ...row,
      latestNote: save.note,
      notesCount: (row.notesCount ?? 0) + (gainsOne ? 1 : 0),
    };
  });
  return changed ? out : rows;
}

/**
 * Lays note saves the server may not have reflected over freshly loaded
 * rows — the Notes column's counterpart to the inline-edit store's
 * `epoch` / `prune`: {@link pruneNoteSaves} with the load's `since`, then
 * {@link overlayNoteSaves} for the saves left.
 */
export function applyLateNoteSaves(
  rows: BatchLead[],
  saves: Map<string, NoteSave>,
  since: number,
): BatchLead[] {
  pruneNoteSaves(saves, since);
  return overlayNoteSaves(rows, saves);
}
